// import PaypalExpressBtn from "react-paypal-express-checkout";
import React, { useState, Fragment } from 'react';
import {
  Button,
  Grid,
  Link,
  Typography,
  Dialog,
  DialogContent,
  SnackbarContent,
} from '@material-ui/core';
import { withLocale } from '../../hocs/withLocale';
import { serviceLocator } from '../../infrastructure/ServiceLocator';
import apiConfig from '../../infrastructure/Config';
import { compose } from 'recompose';
import CircularProgress from '@material-ui/core/CircularProgress';
import LayoutHelper from '../../infrastructure/LayoutHelper';
import moment from 'moment';

interface ClientRatePlan {
  name: string;
  [key: string]: any;
}

interface ClassInfo {
  classDateTime: string;
  studioId: string;
  groupId: string;
  billingTag: string;
  attendancePrice?: number;
  [key: string]: any;
}

interface Studio {
  payPalEnv?: string;
  payPalClientId?: string;
  [key: string]: any;
}

interface StudioClient {
  _id: string;
  [key: string]: any;
}

interface Course {
  repeatableEvent?: {
    onlinePrepay?: number;
    beforeBookingText?: string;
  };
  [key: string]: any;
}

interface Owner {
  _id: string;
  billingTagDefaultPrices?: Record<string, string>;
  liqPayPayment?: any;
  organizationSettings?: any;
  [key: string]: any;
}

interface Domain {
  domain: {
    currencyCode: string;
    [key: string]: any;
  };
}

interface Payment {
  paid?: boolean;
  clientRatePlan?: ClientRatePlan;
  [key: string]: any;
}

interface ClientData {
  clientName?: string;
  phone?: string;
  email?: string;
  [key: string]: any;
}

interface PendingClient {
  _id: string;
  [key: string]: any;
}

interface SignUpConfirmationButtonsProps {
  classInfo: ClassInfo;
  studio: Studio;
  studioClient?: StudioClient;
  disabled?: boolean;
  translate: (key: string, data?: any) => string;
  onSignUp: (payment?: Payment) => void;
  onPaymentFailed: () => void;
  owner: Owner;
  course?: Course;
  domain: Domain;
  userId?: string;
  pendingClientId?: string;
  phone?: string;
  clientData?: ClientData;
  hasRatePlan?: boolean;
  showLoader?: boolean;
}

export const SignUpConfirmationButtons = compose(withLocale)(
  ({
    classInfo,
    studio,
    studioClient,
    disabled,
    translate,
    onSignUp,
    onPaymentFailed,
    owner,
    course,
    domain,
    userId,
    pendingClientId,
    phone,
    clientData,
    hasRatePlan,
    showLoader,
  }: SignUpConfirmationButtonsProps) => {
    const env = studio.payPalEnv || 'sandbox';
    const classDateTime = moment.utc(classInfo.classDateTime);
    const label = (
      <Fragment>{`${translate('studio.class.signUp')} ${classDateTime.format(
        'DD MMM YYYY HH:mm'
      )}`}</Fragment>
    );
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [newPendignClient, setNewPendignClient] = useState<PendingClient | undefined>();
    const [loadingPendingClient, setLoadingPendingClient] = useState<boolean>(false);
    const [estimatingAttendance, setEstimatingAttendance] = useState<boolean>(false);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [clientratePlan, setClientratePlan] = useState<ClientRatePlan | null>(null);
    const buttonsDisabled = disabled || submitting;
    
    const onPaymentSuccess = async (payment: Payment): Promise<void> => {
      if (payment.paid) {
        setSubmitting(true);
        onSignUp(payment);
      }
    };
    
    const estimateAttendance = async (): Promise<void> => {
      setEstimatingAttendance(true);
      const api = serviceLocator.registrationApi;
      api
        .estimateAttendance(classInfo.studioId, classInfo, studioClient)
        .then((data: { payment: Payment }) => {
          setEstimatingAttendance(false);
          let payment = data.payment;

          if (payment && payment.clientRatePlan) {
            setClientratePlan(payment.clientRatePlan);
          }
        }, LayoutHelper.onErr);
    };
    
    const onPaymentCancel = (data: any): void => {
      // User pressed "cancel" or close Paypal's popup!
      console.log('The payment was cancelled!', data);
      onPaymentFailed();
    };

    const onPaymentError = (err: any): void => {
      // The main Paypal's script cannot be loaded or somethings block the loading of that script!
      console.log('Error!', err);
      onPaymentFailed();
    };
    
    const price =
      owner.billingTagDefaultPrices &&
      parseFloat(owner.billingTagDefaultPrices[classInfo.billingTag]);
      
    if (
      owner &&
      owner.liqPayPayment &&
      ((course &&
        course.repeatableEvent &&
        course.repeatableEvent.onlinePrepay && 
        course.repeatableEvent.onlinePrepay > 0) ||
        (price && price > 0))
    ) {
      const priceToPay = price || (course?.repeatableEvent?.onlinePrepay || 0);
      const prePayment =
        course && course.repeatableEvent && course.repeatableEvent.onlinePrepay && 
        course.repeatableEvent.onlinePrepay > 0 ? true : false;
      let liqPayLink = `/liqPay-classInfo/${owner._id}/${classInfo.studioId}/${classInfo.groupId}?prePayment=${prePayment}&amount=${priceToPay}&purpose=ATTENDANCE&subPurpose=${classInfo.billingTag}`;

      if (userId) {
        liqPayLink += `&userId=${userId}`;
      }
      if (pendingClientId) {
        liqPayLink += `&pendingClientId=${pendingClientId}`;
      }
      if (studioClient) {
        liqPayLink += `&studioClientId=${studioClient._id}`;
        return (
          <Fragment>
            <Dialog
              onClose={() => {
                setShowDialog(false);
              }}
              open={showDialog}
            >
              <DialogContent>
                {estimatingAttendance ? (
                  <CircularProgress />
                ) : (
                  <Grid container spacing={1}>
                    {clientratePlan ? (
                      <Fragment>
                        <Grid item xs={12}>
                          <Typography variant="caption">
                            {translate('studio.class.hasRatePlan', {
                              name: clientratePlan.name,
                            })}
                          </Typography>
                          <Button
                            size="large"
                            fullWidth
                            color="primary"
                            variant="contained"
                            onClick={() => {
                              onSignUp();
                            }}
                            disabled={buttonsDisabled}
                          >
                            {(showLoader || submitting) && <CircularProgress />}
                            {translate('studio.class.useRatePlan')}
                          </Button>
                        </Grid>
                      </Fragment>
                    ) : (
                      <Fragment>
                        {course &&
                          course.repeatableEvent &&
                          course.repeatableEvent.beforeBookingText && (
                            <Grid item xs={12}>
                              <SnackbarContent
                                message={
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        course.repeatableEvent
                                          .beforeBookingText,
                                    }}
                                  />
                                }
                              />
                            </Grid>
                          )}
                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            style={{
                              color: '#fff',
                              background: buttonsDisabled
                                ? 'lightgray'
                                : 'rgba(122,183,43,1)',
                            }}
                            component={Link}
                            target="_black"
                            href={liqPayLink}
                          >
                            {translate('studio.class.pay')}({priceToPay}
                            {domain.domain.currencyCode})
                          </Button>
                        </Grid>
                      </Fragment>
                    )}
                  </Grid>
                )}
              </DialogContent>
            </Dialog>
            <Button
              style={{
                padding: '20px',
                fontWeight: 900,
              }}
              size="large"
              fullWidth
              color="primary"
              variant="contained"
              onClick={() => {
                setShowDialog(true);
                estimateAttendance();
              }}
              disabled={buttonsDisabled}
            >
              {label}
            </Button>
          </Fragment>
        );
      }
      return (
        <Fragment>
          <Dialog
            onClose={() => {
              setShowDialog(false);
            }}
            open={showDialog}
          >
            <DialogContent>
              {loadingPendingClient ? (
                <CircularProgress />
              ) : (
                <Grid container spacing={1}>
                  <Fragment>
                    {course &&
                      course.repeatableEvent &&
                      course.repeatableEvent.beforeBookingText && (
                        <Grid item xs={12}>
                          <SnackbarContent
                            message={
                              <div
                                dangerouslySetInnerHTML={{
                                  __html:
                                    course.repeatableEvent.beforeBookingText,
                                }}
                              />
                            }
                          />
                        </Grid>
                      )}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        style={{
                          padding: '20px',
                          fontWeight: 900,
                          color: '#fff',
                          background: buttonsDisabled
                            ? 'lightgray'
                            : 'rgba(122,183,43,1)',
                        }}
                        component={Link}
                        target="_black"
                        href={`${liqPayLink}${
                          newPendignClient
                            ? '&pendingClientId=' + newPendignClient._id
                            : ''
                        }`}
                      >
                        {translate('studio.class.pay')}({priceToPay}
                        {domain.domain.currencyCode})
                      </Button>
                    </Grid>
                  </Fragment>
                </Grid>
              )}
            </DialogContent>
          </Dialog>
          <Button
            style={{
              padding: '20px',
              fontWeight: 900,
            }}
            size="large"
            fullWidth
            color="primary"
            variant="contained"
            onClick={async () => {
              setShowDialog(true);
              if (!pendingClientId && !userId) {
                setLoadingPendingClient(true);
                const pendignClient =
                  await serviceLocator.onlineRegistrationsApiCall.addPendingClient(
                    {
                      classInfo: classInfo,
                      client: clientData,
                      ownerId: owner._id,
                      studioId: classInfo.studioId,
                    }
                  );
                setLoadingPendingClient(false);
                setNewPendignClient(pendignClient);
              }
            }}
            disabled={buttonsDisabled}
          >
            {label}
          </Button>
        </Fragment>
      );
    }

    if (studio.payPalClientId && classInfo.attendancePrice && classInfo.attendancePrice > 0)
      return (
        <Grid
          container
          alignItems="flex-start"
          style={{ position: 'relative' }}
        >
          {buttonsDisabled && (
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: 1000,
                backgroundColor: '#fff',
                opacity: 0.75,
              }}
            />
          )}
          {/*<PaypalExpressBtn onSuccess={onPaymentSuccess}*/}
          {/*                  onCancel={onPaymentCancel}*/}
          {/*                  onError={onPaymentError}*/}
          {/*                  style={{*/}
          {/*                      color: 'blue',*/}
          {/*                      shape: 'pill',*/}
          {/*                      label: 'pay',*/}
          {/*                      height: 40*/}
          {/*                  }}*/}
          {/*                  client={{*/}
          {/*                      [env]: studio.payPalClientId,*/}
          {/*                  }} currency={'USD'} total={classInfo.attendancePrice} shipping={1} env={env}/>*/}
          <Button
            fullWidth
            size="large"
            color="primary"
            disabled={buttonsDisabled}
            onClick={() => {
              onSignUp();
            }}
          >
            Pay later and {label}
          </Button>
        </Grid>
      );
    return (
      <Button
        size="large"
        style={{
          padding: '20px',
          fontWeight: 900,
        }}
        fullWidth
        color="primary"
        variant="contained"
        onClick={() => {
          onSignUp();
        }}
        disabled={buttonsDisabled}
      >
        {(showLoader || submitting) && <CircularProgress />}
        {label}
      </Button>
    );
  }
);